import { Box, Typography, useTheme } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import {
    TextItem,
    UserContext,
    UserContextListField,
} from "models/ClientBrain/UserContext";
import React from "react";

export const LIST_FIELDS: { field: UserContextListField; label: string }[] = [
    { field: "goals", label: "Goals" },
    { field: "responsibilities", label: "Responsibilities" },
    { field: "successMetrics", label: "Success Metrics" },
    { field: "currentConcerns", label: "Current Concerns" },
];

export const collapseItemText = (items: TextItem[]): string =>
    items
        .map((item) => item.text.trim())
        .filter(Boolean)
        .join("\n\n");

const DetailBlock: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => {
    const theme = useTheme();
    return (
        <Box sx={{ marginBottom: theme.spacing(1.75) }}>
            <Typography
                sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: theme.palette.gray[400],
                    marginBottom: theme.spacing(0.5),
                }}
            >
                {label}
            </Typography>
            {children}
        </Box>
    );
};

type Props = {
    ctx: UserContext;
    isEditing: boolean;
    onSetRole: (value: string) => void;
    onSetListFieldText: (field: UserContextListField, text: string) => void;
    /** Prefix used to build unique field element ids (e.g. the viewed user's id). */
    keyPrefix: string;
};

/**
 * Job Role + the four free-text list fields (Goals, Responsibilities, Success Metrics,
 * Current Concerns) for a `UserContext`. Shared by the admin Client Brain "User Profiles"
 * editor and the self-service KBo Settings "User settings" page so both surfaces render
 * (and thus save) the same fields identically.
 */
const UserContextFieldsEditor: React.FC<Props> = ({
    ctx,
    isEditing,
    onSetRole,
    onSetListFieldText,
    keyPrefix,
}) => {
    const theme = useTheme();

    return (
        <>
            <DetailBlock label="Job Role">
                {isEditing ? (
                    <AcxMainTextField
                        id={`user-context-role-${keyPrefix}`}
                        value={ctx.role}
                        onChange={(e) => onSetRole(e.target.value)}
                        fullWidth
                    />
                ) : (
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: ctx.role
                                ? theme.palette.gray[700]
                                : theme.palette.gray[400],
                            fontStyle: ctx.role ? "normal" : "italic",
                        }}
                    >
                        {ctx.role || "Not set"}
                    </Typography>
                )}
            </DetailBlock>

            {LIST_FIELDS.map(({ field, label }) => (
                <DetailBlock key={field} label={label}>
                    <AcxMainTextField
                        id={`user-context-${field}-${keyPrefix}`}
                        value={collapseItemText(ctx[field])}
                        onChange={(e) =>
                            onSetListFieldText(field, e.target.value)
                        }
                        multiline
                        rowsMin={3}
                        rowsMax={10}
                        resizable
                        placeholderText="Not set"
                        isDisabled={!isEditing}
                        fullWidth
                    />
                </DetailBlock>
            ))}
        </>
    );
};

export default UserContextFieldsEditor;
